import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../lib/api';
import { 
  FiHome, FiPackage, FiCalendar, FiPhone, 
  FiSettings, FiLogOut, FiMenu, FiX,
  FiGrid, FiUsers, FiGlobe, FiShield,
  FiEdit3, FiImage, FiSearch, FiStar, FiFileText, FiGift,
  FiExternalLink, FiBookOpen, FiTruck, FiTarget, FiMail, FiCreditCard,
  FiTool, FiList, FiChevronDown, FiChevronRight, FiMap,
  FiDownload, FiLock, FiShoppingCart, FiLayers, FiTag, FiPercent, FiLayout, FiFile,
  FiUserCheck, FiTrash2, FiAlertTriangle, FiRefreshCw
} from 'react-icons/fi';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Helper function to get API URL - uses relative path in production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('justasmile-events.com') || hostname.includes('app.justasmile')) {
      return '';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getApiUrl();

// ====== NOUVELLE STRUCTURE MENU BACK-OFFICE ======

// 🏠 ACCUEIL
const accueilItems = [
  { path: '/dashboard', icon: <FiHome />, label: 'Tableau de bord', exact: true },
];

// 📦 LOGISTIQUE - Gestion opérationnelle
const logistiqueItems = [
  { path: '/dashboard/reservations', icon: <FiCalendar />, label: 'Réservations' },
  { path: '/dashboard/planning', icon: <FiGrid />, label: 'Planning' },
  { path: '/dashboard/contracts', icon: <FiFileText />, label: 'Contrats' },
  { path: '/dashboard/equipment', icon: <FiTool />, label: 'Équipements' },
];

// 📊 GESTION - Produits, Clients, Finances
const gestionItems = [
  { path: '/dashboard/products-formulas', icon: <FiLayers />, label: 'Produits & Formules' },
  { path: '/dashboard/gift-cards', icon: <FiGift />, label: 'Cartes Cadeaux' },
  { path: '/dashboard/gift-card-requests', icon: <FiGift />, label: 'Demandes Cartes', badge: 'Nouveau' },
  { path: '/dashboard/promo-codes', icon: <FiPercent />, label: 'Codes Promo' },
  { path: '/dashboard/customers', icon: <FiUsers />, label: 'Clients (CRM)' },
  { path: '/dashboard/invoices', icon: <FiFileText />, label: 'Factures' },
  { path: '/dashboard/payment-methods', icon: <FiCreditCard />, label: 'Moyens de Paiement' },
];

// 🌐 SITE - Contenus et Médias
const siteItems = [
  { path: '/dashboard/content', icon: <FiImage />, label: 'Contenu (Logos, Modèles, Vidéos)', badge: 'Simple' },
  { path: '/dashboard/constructor', icon: <FiLayout />, label: 'Constructor', badge: 'Avancé' },
  { path: '/dashboard/pages', icon: <FiFile />, label: 'Pages du Site', badge: 'Obsolète', disabled: true },
  { path: '/dashboard/site-settings', icon: <FiEdit3 />, label: 'Paramètres Site' },
  { path: '/dashboard/media', icon: <FiImage />, label: 'Médiathèque' },
  { path: '/dashboard/blog', icon: <FiBookOpen />, label: 'Articles Blog' },
];

// ⚙️ CONFIGURATION - Paramètres généraux
const configItems = [
  { path: '/dashboard/settings', icon: <FiSettings />, label: 'Infos Entreprise' },
  { path: '/dashboard/team', icon: <FiUsers />, label: 'Équipe' },
  { path: '/dashboard/contracts/template', icon: <FiFileText />, label: 'Conditions Location' },
  { path: '/dashboard/email-templates', icon: <FiMail />, label: 'Modèles Emails' },
  { path: '/dashboard/email-config', icon: <FiMail />, label: 'Config. SMTP' },
  { path: '/dashboard/delivery-config', icon: <FiTruck />, label: 'Frais Km & Livraison' },
];

// 🔧 TECHNIQUE - Logs et données
const techniqueItems = [
  { path: '/dashboard/email-logs', icon: <FiMail />, label: 'Historique Emails' },
  { path: '/dashboard/backlog', icon: <FiList />, label: 'Backlog & Changelog' },
  { path: '/dashboard/export', icon: <FiDownload />, label: 'Export Données' },
];

// 📈 SEO & RÉFÉRENCEMENT
const seoItems = [
  { path: '/dashboard/seo', icon: <FiSearch />, label: 'SEO & Référencement' },
  { path: '/dashboard/google-reviews', icon: <FiStar />, label: 'Avis Google' },
  { path: '/dashboard/sitemap', icon: <FiMap />, label: 'Sitemap' },
];

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    accueil: true,
    logistique: true,
    gestion: false,
    site: false,
    config: false,
    technique: false,
    seo: false
  });
  const [shopSettings, setShopSettings] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [showVersionTooltip, setShowVersionTooltip] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetSelections, setResetSelections] = useState({
    clients: false,
    reservations: false,
    contracts: false,
    gift_card_requests: false
  });
  const [isResetting, setIsResetting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, isCollaborator } = useAuth();
  
  // Ref pour préserver la position de scroll du menu
  const navRef = useRef(null);
  const scrollPositionRef = useRef(0);
  
  // Sauvegarder la position du scroll avant navigation
  const saveScrollPosition = useCallback(() => {
    if (navRef.current) {
      scrollPositionRef.current = navRef.current.scrollTop;
    }
  }, []);
  
  // Restaurer la position du scroll après navigation
  useEffect(() => {
    if (navRef.current && scrollPositionRef.current > 0) {
      navRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [location.pathname]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRes = await settingsAPI.get();
        if (settingsRes.data) setShopSettings(settingsRes.data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  // Auto-expand menu based on current path
  useEffect(() => {
    const path = location.pathname;
    
    if (accueilItems.some(item => path === item.path)) {
      setExpandedMenus(prev => ({ ...prev, accueil: true }));
    }
    if (logistiqueItems.some(item => path === item.path || path.startsWith(item.path + '/'))) {
      setExpandedMenus(prev => ({ ...prev, logistique: true }));
    }
    if (gestionItems.some(item => path === item.path || path.startsWith(item.path + '/'))) {
      setExpandedMenus(prev => ({ ...prev, gestion: true }));
    }
    if (siteItems.some(item => path === item.path || path.startsWith(item.path + '/'))) {
      setExpandedMenus(prev => ({ ...prev, site: true }));
    }
    if (configItems.some(item => path === item.path || path.startsWith(item.path + '/'))) {
      setExpandedMenus(prev => ({ ...prev, config: true }));
    }
    if (techniqueItems.some(item => path === item.path || path.startsWith(item.path + '/'))) {
      setExpandedMenus(prev => ({ ...prev, technique: true }));
    }
    if (seoItems.some(item => path === item.path || path.startsWith(item.path + '/'))) {
      setExpandedMenus(prev => ({ ...prev, seo: true }));
    }
  }, [location.pathname]);

  // Load version info
  useEffect(() => {
    const loadVersionInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/versioning/current`);
        if (res.ok) {
          const data = await res.json();
          setVersionInfo(data);
        }
      } catch (error) {
        console.error('Error loading version info:', error);
      }
    };
    loadVersionInfo();
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const getUserRole = () => {
    if (user?.is_super_admin || user?.role === 'super_admin') return 'Super Admin';
    if (user?.role === 'collaborator') return 'Collaborateur';
    if (user?.role === 'entreprise') return 'Entreprise';
    return 'Client';
  };

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Reset data function
  const handleResetData = async () => {
    const selectedItems = Object.entries(resetSelections)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => key);
    
    if (selectedItems.length === 0) {
      toast.error('Veuillez sélectionner au moins une option');
      return;
    }

    const confirmMessage = `Êtes-vous sûr de vouloir supprimer définitivement :\n${
      selectedItems.map(item => `• ${item === 'clients' ? 'Clients' : item === 'reservations' ? 'Réservations' : 'Contrats'}`).join('\n')
    }\n\nCette action est irréversible !`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/reset-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ collections: selectedItems })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Données supprimées : ${result.deleted_counts ? Object.entries(result.deleted_counts).map(([k,v]) => `${k}: ${v}`).join(', ') : 'Terminé'}`);
        setShowResetModal(false);
        setResetSelections({ clients: false, reservations: false, contracts: false, gift_card_requests: false });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setIsResetting(false);
    }
  };

  const MenuItem = ({ item }) => {
    const active = isActive(item.path, item.exact);
    const disabled = item.disabled;
    
    return (
      <Link
        to={disabled ? '#' : item.path}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            toast.info('Cette fonctionnalité est désactivée');
          } else {
            // Sauvegarder la position du scroll avant navigation
            saveScrollPosition();
            setSidebarOpen(false);
          }
        }}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
          disabled 
            ? 'text-white/30 cursor-not-allowed'
            : active
              ? 'bg-[#d4af37] text-black font-medium'
              : 'text-white/70 hover:bg-white/10 hover:text-white'
        }`}
      >
        <span className="text-base">{item.icon}</span>
        <span>{item.label}</span>
        {disabled && <FiLock className="ml-auto h-3 w-3" />}
      </Link>
    );
  };

  const MenuSection = ({ title, items, menuKey, emoji }) => {
    const isExpanded = expandedMenus[menuKey];
    const hasActiveItem = items.some(item => isActive(item.path, item.exact));
    
    return (
      <div className="mb-1">
        <button
          onClick={() => toggleMenu(menuKey)}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm ${
            hasActiveItem ? 'bg-white/10' : 'hover:bg-white/5'
          }`}
        >
          <div className="flex items-center gap-2">
            <span>{emoji}</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-white/80">
              {title}
            </span>
          </div>
          {isExpanded ? (
            <FiChevronDown className="h-4 w-4 text-white/50" />
          ) : (
            <FiChevronRight className="h-4 w-4 text-white/50" />
          )}
        </button>
        {isExpanded && (
          <div className="ml-2 mt-1 space-y-0.5">
            {items.map((item, idx) => (
              <MenuItem key={idx} item={item} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const Sidebar = () => (
    <div className="h-full flex flex-col bg-slate-900">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img 
            src={shopSettings?.logo_url || "https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png"}
            alt="Just A Smile Events"
            className="w-12 h-12 rounded-lg object-contain bg-black p-1"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-semibold text-sm truncate">
              {shopSettings?.shop_name || 'Just A Smile Events'}
            </h2>
            <p className="text-white/50 text-xs">{getUserRole()}</p>
          </div>
        </div>
        
        {/* View Site Button */}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors"
        >
          <FiExternalLink className="w-4 h-4" />
          Voir le site
        </a>
        
        {/* Reset Data Button - Admin only */}
        {!isCollaborator && (
          <button
            onClick={() => setShowResetModal(true)}
            className="flex items-center justify-center gap-2 mt-2 w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm rounded-lg transition-colors border border-red-500/20"
          >
            <FiTrash2 className="w-4 h-4" />
            Remise à zéro
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="flex-1 p-3 overflow-y-auto scroll-smooth">
        {/* Collaborator simplified view */}
        {isCollaborator ? (
          <>
            {/* Only show Reservations for collaborators */}
            <div className="mb-4">
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/50">
                📋 Mes Réservations
              </div>
              <Link
                to="/dashboard/reservations"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                  location.pathname === '/dashboard/reservations'
                    ? 'bg-[#d4af37] text-black font-medium'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <FiCalendar className="text-base" />
                <span>Réservations assignées</span>
              </Link>
              <Link
                to="/dashboard/planning"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                  location.pathname === '/dashboard/planning'
                    ? 'bg-[#d4af37] text-black font-medium'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <FiGrid className="text-base" />
                <span>Planning</span>
              </Link>
            </div>
            
            {/* Info box for collaborators */}
            <div className="mx-2 p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-xs text-white/60 leading-relaxed">
                👋 Bienvenue ! Vous avez accès aux réservations qui vous sont assignées et au planning.
              </p>
            </div>
          </>
        ) : (
          <>
            {/* 🏠 Accueil */}
            <div className="mb-1">
              {accueilItems.map((item, idx) => (
                <MenuItem key={idx} item={item} />
              ))}
            </div>
            
            <div className="border-t border-white/10 my-2" />
            
            {/* 📦 Logistique */}
            <MenuSection 
              title="Logistique" 
              items={logistiqueItems}
              menuKey="logistique"
              emoji="📦"
            />
            
            {/* 📊 Gestion */}
            <MenuSection 
              title="Gestion" 
              items={gestionItems}
              menuKey="gestion"
              emoji="📊"
            />
            
            {/* 🌐 Site */}
            <MenuSection 
              title="Site" 
              items={siteItems}
              menuKey="site"
              emoji="🌐"
            />
            
            {/* ⚙️ Configuration */}
            <MenuSection 
              title="Configuration" 
              items={configItems}
              menuKey="config"
              emoji="⚙️"
            />
            
            {/* 🔧 Technique */}
            <MenuSection 
              title="Technique" 
              items={techniqueItems}
              menuKey="technique"
              emoji="🔧"
            />
            
            {/* 📈 SEO & Référencement */}
            <MenuSection 
              title="SEO & Référencement" 
              items={seoItems}
              menuKey="seo"
              emoji="📈"
            />
            
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/10">
        {/* Version Info with click to show deployment time */}
        <div 
          className="mb-3 px-3 py-2 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors relative"
          onClick={() => setShowVersionTooltip(!showVersionTooltip)}
        >
          <p className="text-white/40 text-xs text-center">
            v{versionInfo?.version || '2.18.0'} • {versionInfo?.deployed_at 
              ? new Date(versionInfo.deployed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
              : new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
          </p>
          {showVersionTooltip && versionInfo?.deployed_at && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg shadow-lg z-50 whitespace-nowrap">
              <p className="text-white text-xs font-medium">Déployé le {new Date(versionInfo.deployed_at).toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric' 
              })}</p>
              <p className="text-amber-400 text-xs">à {new Date(versionInfo.deployed_at).toLocaleTimeString('fr-FR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}</p>
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-800" />
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#d4af37] flex items-center justify-center text-black font-bold text-sm">
            {user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors text-sm"
        >
          <FiLogOut />
          Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={shopSettings?.logo_url || "https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png"}
              alt="Just A Smile Events"
              className="w-8 h-8 rounded object-contain"
            />
            <span className="text-white font-semibold text-sm">Just A Smile Events</span>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white p-2"
          >
            {sidebarOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 bottom-0 left-0 z-50 w-64
        transform transition-transform duration-300
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 h-screen pt-16 lg:pt-0 overflow-hidden">
        <div className="h-full overflow-y-auto p-2 sm:p-4 lg:p-8">
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-6 min-h-[calc(100vh-6rem)] overflow-x-auto">
            {children}
          </div>
        </div>
      </main>

      {/* Reset Data Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <FiAlertTriangle className="h-5 w-5" />
              Remise à zéro des données
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les données à supprimer définitivement. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            {/* Clients checkbox */}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={resetSelections.clients}
                onChange={(e) => setResetSelections(prev => ({ ...prev, clients: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-900">Clients</p>
                <p className="text-sm text-slate-500">Supprimer tous les clients enregistrés</p>
              </div>
              <FiUsers className="h-5 w-5 text-slate-400" />
            </label>

            {/* Reservations checkbox */}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={resetSelections.reservations}
                onChange={(e) => setResetSelections(prev => ({ ...prev, reservations: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-900">Réservations</p>
                <p className="text-sm text-slate-500">Supprimer toutes les réservations</p>
              </div>
              <FiCalendar className="h-5 w-5 text-slate-400" />
            </label>

            {/* Contracts checkbox */}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={resetSelections.contracts}
                onChange={(e) => setResetSelections(prev => ({ ...prev, contracts: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-900">Contrats</p>
                <p className="text-sm text-slate-500">Supprimer tous les contrats générés</p>
              </div>
              <FiFileText className="h-5 w-5 text-slate-400" />
            </label>

            {/* Gift Card Requests checkbox */}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={resetSelections.gift_card_requests}
                onChange={(e) => setResetSelections(prev => ({ ...prev, gift_card_requests: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-900">Demandes de carte cadeau</p>
                <p className="text-sm text-slate-500">Supprimer toutes les demandes de carte cadeau</p>
              </div>
              <FiGift className="h-5 w-5 text-slate-400" />
            </label>
          </div>

          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 flex items-start gap-2">
              <FiAlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Attention : Cette action supprimera définitivement les données sélectionnées. Pensez à créer un snapshot avant de continuer.</span>
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowResetModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleResetData}
              disabled={isResetting || !Object.values(resetSelections).some(v => v)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isResetting ? (
                <>
                  <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <FiTrash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
